import React, { useState } from 'react';
import Layout from '../components/Layout';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { useAuth } from '../contexts/AuthContext';
import { useDriverTrips } from '../hooks/useDriverTrips';
import { useDriverLocation } from '../hooks/useDriverLocation';
import { useDrivingSessions } from '../hooks/useDrivingSessions';
import { useWalletItems } from '../hooks/useWalletItems';
import { useTransactions } from '../hooks/useTransactions';
import { useIncomeByApp } from '../hooks/useIncomeByApp';
import { useDriverApps } from '../hooks/useDriverApps';
import { supabase } from '../lib/supabase';
import {
  Car, DollarSign, Clock, MapPin, CheckCircle,
  XCircle, TrendingUp, Star, Navigation,
  ShieldCheck, ShieldAlert, ShieldX,
  CreditCard, User, Lock, Unlock, AlertTriangle,
} from 'lucide-react';
import type { Trip, DrivingSession, WalletItem, ComplianceStatus } from '../lib/types';

const MAP_FALLBACK: [number, number] = [59.3293, 18.0686];

const STATUS_COLORS: Record<Trip['status'], string> = {
  pending:   'bg-slate-100 text-slate-600',
  active:    'bg-blue-50 text-blue-600',
  completed: 'bg-emerald-50 text-emerald-600',
  cancelled: 'bg-red-50 text-red-600',
};

function Skeleton({ className }: { className?: string }) {
  return <div className={`skeleton rounded-xl ${className ?? ''}`} />;
}

// ── Compliance helpers (Vägarbetstidslagen 2005:395) ─────────────────────────

const breakDurationMin = (b: { started_at: string; ended_at: string | null }) => {
  if (!b.ended_at) return 0;
  return Math.round((new Date(b.ended_at).getTime() - new Date(b.started_at).getTime()) / 60000);
};

// Local calendar day (local midnight) — MUST match the mobile app's
// startOfDay() so night-shift sessions land on the same day on both surfaces.
const pad2 = (n: number) => String(n).padStart(2, '0');
const localDayKey = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const formatDuration = (minutes: number) =>
  minutes <= 0 ? '0 min'
  : minutes < 60 ? `${minutes} min`
  : `${Math.floor(minutes / 60)}h ${minutes % 60}m`;

const getComplianceStatus = (todayMinutes: number, maxSingleBreakMin: number): ComplianceStatus => {
  if (todayMinutes > 780) return 'violation';
  if (todayMinutes > 540 && maxSingleBreakMin < 45) return 'warning'; // >9h needs 45-min break
  if (todayMinutes > 360 && maxSingleBreakMin < 30) return 'warning'; // >6h needs 30-min break
  return 'ok';
};

const COMPLIANCE_CONFIG: Record<ComplianceStatus, {
  label: string; bg: string; text: string; border: string; Icon: React.ComponentType<{ className?: string }>;
}> = {
  ok:        { label: 'Godkänd',    bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', Icon: ShieldCheck },
  warning:   { label: 'Varning',    bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200',   Icon: ShieldAlert },
  violation: { label: 'Regelbrott', bg: 'bg-red-50',     text: 'text-red-700',     border: 'border-red-200',     Icon: ShieldX },
};

function CompliancePill({ status }: { status: ComplianceStatus }) {
  const c = COMPLIANCE_CONFIG[status];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-lg border font-semibold px-3 py-1.5 text-sm ${c.bg} ${c.text} ${c.border}`}>
      <c.Icon className="w-4 h-4" />{c.label}
    </span>
  );
}

// ── Wallet expiry helper ─────────────────────────────────────────────────────

function ExpiryBadge({ expiresAt }: { expiresAt: string | null }) {
  if (!expiresAt) return <span className="text-xs text-slate-400">Inget datum</span>;
  const daysLeft = Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86_400_000);
  if (daysLeft < 0) return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 border border-red-100 rounded-md px-2 py-0.5">
      <AlertTriangle className="w-3 h-3" />Utgången
    </span>
  );
  if (daysLeft <= 30) return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 bg-amber-50 border border-amber-100 rounded-md px-2 py-0.5">
      <AlertTriangle className="w-3 h-3" />Snart utgångna ({daysLeft}d)
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 bg-emerald-50 border border-emerald-100 rounded-md px-2 py-0.5">
      <CheckCircle className="w-3 h-3" />Giltig
    </span>
  );
}

const WALLET_KIND_CONFIG: Record<WalletItem['kind'], {
  label: string; color: string; bg: string; Icon: React.ComponentType<{ className?: string }>;
}> = {
  'driver-license': { label: 'Körkort',          color: 'text-blue-600',   bg: 'bg-blue-50',   Icon: Car },
  'id-card':        { label: 'ID-kort',           color: 'text-slate-600',  bg: 'bg-slate-100', Icon: User },
  'taxi-license':   { label: 'Taxilegitimation',  color: 'text-emerald-600',bg: 'bg-emerald-50',Icon: ShieldCheck },
  'payment-card':   { label: 'Betalkort',         color: 'text-purple-600', bg: 'bg-purple-50', Icon: CreditCard },
};

// ── Main component ───────────────────────────────────────────────────────────

type TabId = 'overview' | 'trips' | 'income' | 'vilotid' | 'planbok';

const DriverPortal: React.FC = () => {
  const { user, role } = useAuth();
  const [driverId, setDriverId] = useState<string | undefined>(undefined);
  const [loadingDriver, setLoadingDriver] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [expandedSession, setExpandedSession] = useState<string | null>(null);

  React.useEffect(() => {
    if (!user) return;
    supabase.from('drivers').select('id').eq('user_id', user.id).maybeSingle().then(({ data }) => {
      setDriverId(data?.id ?? undefined);
      setLoadingDriver(false);
    });
  }, [user]);

  const { trips, loading: tripsLoading }     = useDriverTrips(driverId);
  const { location }                         = useDriverLocation(driverId);
  const { sessions, loading: sessionsLoading } = useDrivingSessions(driverId);
  const { items: walletItems, loading: walletLoading } = useWalletItems(driverId);
  const { transactions, loading: txLoading } = useTransactions(driverId);
  const { rows: incomeByApp } = useIncomeByApp(driverId);
  const { apps: driverApps } = useDriverApps(driverId);

  const completed   = trips.filter(t => t.status === 'completed');
  const cancelled   = trips.filter(t => t.status === 'cancelled');
  const active      = trips.filter(t => t.status === 'active');
  const totalIncome = completed.reduce((s, t) => s + (t.fare ?? 0), 0);
  const avgPerTrip  = completed.length > 0 ? totalIncome / completed.length : 0;
  const mapCenter: [number, number] = location ? [location.lat, location.lng] : MAP_FALLBACK;

  // Compliance — today (local midnight, same day boundary as the mobile app)
  const today         = localDayKey(new Date());
  const todaySessions = sessions.filter(s => localDayKey(new Date(s.started_at)) === today);
  const todayMinutes  = todaySessions.reduce((sum, s) => sum + (s.driving_minutes ?? 0), 0);
  const todayBreaks   = todaySessions.flatMap(s => s.breaks ?? []);
  const todayBreakMin = todayBreaks.reduce((sum, b) => sum + breakDurationMin(b), 0);
  const maxSingleBreak = todayBreaks.reduce((m, b) => Math.max(m, breakDurationMin(b)), 0);
  const complianceStatus = getComplianceStatus(todayMinutes, maxSingleBreak);

  const tabs: { id: TabId; label: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: 'Översikt',         icon: <TrendingUp className="w-4 h-4" /> },
    { id: 'trips',    label: 'Mina Resor',        icon: <Car className="w-4 h-4" /> },
    { id: 'income',   label: 'Inkomst',           icon: <DollarSign className="w-4 h-4" /> },
    { id: 'vilotid',  label: 'Vilotidsrapport',   icon: <Clock className="w-4 h-4" /> },
    { id: 'planbok',  label: 'DG-Plånbok',        icon: <CreditCard className="w-4 h-4" /> },
  ];

  if (loadingDriver) return null;

  if (!driverId) {
    return (
      <Layout title="Förarportal" breadcrumbs={[{ label: 'Förarportal' }]}>
        <div className="card p-12 flex flex-col items-center text-center">
          <Car className="w-12 h-12 text-slate-300 mb-4" />
          <h3 className="font-bold text-slate-700 text-lg mb-2">Ingen förarprofil kopplad</h3>
          <p className="text-sm text-slate-400 max-w-sm">
            Ditt konto ({user?.email}) är inte kopplat till ett förarrekord.
            Kontakta din företagsadmin.
          </p>
          <div className="mt-4 px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-xs font-mono text-slate-500">
            Auth ID: {user?.id}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Förarportal" breadcrumbs={[{ label: 'Hem' }, { label: 'Förarportal' }]}>
      <div className="space-y-5">

        {/* Welcome banner */}
        <div className="rounded-2xl p-5 text-white overflow-hidden relative"
          style={{ background: 'linear-gradient(135deg, #0B1120, #0f2044)' }}>
          <div className="absolute top-0 right-0 w-48 h-48 rounded-full -translate-y-16 translate-x-16"
            style={{ background: 'rgba(6,182,212,0.08)' }} />
          <div className="relative">
            <p className="text-white/50 text-xs font-medium uppercase tracking-widest mb-1">Välkommen tillbaka</p>
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
              {user?.name ?? 'Förare'}
            </h2>
            <p className="text-white/50 text-sm mt-1">Roll: <span className="text-electric-400 capitalize">{role}</span></p>
          </div>
          {location && (
            <div className="relative mt-3 flex items-center gap-1.5 text-xs text-white/60">
              <Navigation className="w-3.5 h-3.5 text-electric-400" />
              GPS live: {location.lat.toFixed(4)}, {location.lng.toFixed(4)}
              {location.speed != null && <> · {location.speed.toFixed(0)} km/h</>}
            </div>
          )}
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Totala resor',  value: trips.length,               icon: <Car className="w-5 h-5" />,          color: '#06B6D4' },
            { label: 'Avslutade',     value: completed.length,            icon: <CheckCircle className="w-5 h-5" />,  color: '#10b981' },
            { label: 'Avbrutna',      value: cancelled.length,            icon: <XCircle className="w-5 h-5" />,     color: '#ef4444' },
            { label: 'Total inkomst', value: `${totalIncome.toFixed(0)} kr`, icon: <DollarSign className="w-5 h-5" />, color: '#F59E0B' },
          ].map((s, i) => (
            <div key={i} className="card p-5 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: `${s.color}18`, color: s.color }}>
                {s.icon}
              </div>
              <div>
                {tripsLoading ? <Skeleton className="h-6 w-12 mb-1" /> : (
                  <div className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk' }}>{s.value}</div>
                )}
                <div className="text-xs text-slate-500">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="card overflow-hidden">
          <div className="border-b border-slate-100 flex overflow-x-auto">
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium transition-colors relative shrink-0 ${
                  activeTab === tab.id ? 'text-electric-600' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}>
                {tab.icon}{tab.label}
                {activeTab === tab.id && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-electric-500 rounded-t" />}
              </button>
            ))}
          </div>

          <div className="p-5">

            {/* ── Översikt ── */}
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div>
                  <h3 className="font-semibold text-slate-700 text-sm mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-electric-500" /> Min position
                  </h3>
                  <div className="h-52 rounded-xl overflow-hidden border border-slate-100">
                    <MapContainer center={mapCenter} zoom={13} style={{ height: '100%', width: '100%' }}>
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; OpenStreetMap' />
                      {location && (
                        <Marker position={[location.lat, location.lng]}>
                          <Popup>
                            <strong>Min position</strong>
                            {location.speed != null && <p className="text-xs text-gray-500">{location.speed.toFixed(0)} km/h</p>}
                          </Popup>
                        </Marker>
                      )}
                    </MapContainer>
                  </div>
                  {!location && <p className="text-xs text-center text-slate-400 mt-2">Ingen GPS-data ännu</p>}
                </div>

                <div className="space-y-3">
                  <h3 className="font-semibold text-slate-700 text-sm flex items-center gap-2">
                    <Star className="w-4 h-4 text-amber-400" /> Prestandaöversikt
                  </h3>
                  {[
                    { label: 'Aktiva resor just nu',  value: active.length > 0 ? `${active.length} aktiva` : 'Inte på resa', icon: <Car className="w-4 h-4" /> },
                    { label: 'Genomsnitt per resa',   value: avgPerTrip > 0 ? `${avgPerTrip.toFixed(0)} kr` : '—', icon: <DollarSign className="w-4 h-4" /> },
                    { label: 'Genomförandegrad',      value: trips.length > 0 ? `${Math.round((completed.length / trips.length) * 100)}%` : '—', icon: <CheckCircle className="w-4 h-4" /> },
                    { label: 'Senaste resa',          value: completed[0] ? new Date(completed[0].created_at).toLocaleDateString('sv-SE') : '—', icon: <Clock className="w-4 h-4" /> },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-3.5 rounded-xl border border-slate-100 bg-white hover:shadow-sm transition">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-lg bg-electric-50 text-electric-500 flex items-center justify-center shrink-0">
                          {item.icon}
                        </div>
                        <span className="text-sm text-slate-600">{item.label}</span>
                      </div>
                      <span className="text-sm font-semibold text-slate-800">
                        {tripsLoading ? '...' : item.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Mina Resor ── */}
            {activeTab === 'trips' && (
              <div>
                {tripsLoading ? (
                  <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
                ) : trips.length === 0 ? (
                  <div className="py-10 text-center text-sm text-slate-400">Inga resor registrerade ännu</div>
                ) : (
                  <div className="rounded-xl border border-slate-100 overflow-hidden overflow-x-auto">
                    <table className="min-w-full table-premium text-sm">
                      <thead><tr><th>Datum</th><th>Rutt</th><th>Km</th><th>Status</th><th>Pris</th></tr></thead>
                      <tbody>
                        {trips.map(t => (
                          <tr key={t.id}>
                            <td className="text-xs text-slate-400 whitespace-nowrap">
                              {new Date(t.created_at).toLocaleDateString('sv-SE')}
                            </td>
                            <td className="max-w-[180px] truncate text-xs">
                              {t.origin_address ?? '—'} → {t.dest_address ?? '—'}
                            </td>
                            <td className="text-xs">{t.distance_km != null ? `${t.distance_km.toFixed(1)} km` : '—'}</td>
                            <td>
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[t.status]}`}>
                                {t.status}
                              </span>
                            </td>
                            <td className="font-semibold">{t.fare != null ? `${t.fare.toFixed(0)} kr` : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ── Inkomst ── */}
            {activeTab === 'income' && (
              <div className="space-y-5">
                {/* Income per app — mirrors mobile Ekonomi */}
                {incomeByApp.length > 0 && (
                  <div className="rounded-xl border border-slate-100 overflow-hidden">
                    <div className="bg-slate-50 px-4 py-3 border-b border-slate-100">
                      <h3 className="text-sm font-semibold text-slate-700">Inkomst per app</h3>
                    </div>
                    <div className="divide-y divide-slate-50">
                      {incomeByApp.map(row => (
                        <div key={row.app} className="px-4 py-3 flex justify-between items-center text-sm">
                          <span className="font-medium text-slate-700">{row.app} · {row.trips} resor</span>
                          <span className="font-bold text-emerald-600">{row.total.toFixed(0)} kr</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Connected apps */}
                {driverApps.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {driverApps.map(app => (
                      <span key={app.id} className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                        app.status === 'connected'
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : 'bg-slate-50 text-slate-600 border-slate-200'
                      }`}>
                        {app.app_name}
                      </span>
                    ))}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { label: 'Totalt intjänat', value: `${totalIncome.toFixed(0)} kr`, color: 'text-emerald-600' },
                    { label: 'Snitt per resa',  value: avgPerTrip > 0 ? `${avgPerTrip.toFixed(0)} kr` : '—', color: 'text-electric-600' },
                    { label: 'Betalda resor',   value: completed.length, color: 'text-slate-800' },
                  ].map((s, i) => (
                    <div key={i} className="p-4 rounded-xl border border-slate-100 bg-white text-center">
                      <div className={`text-2xl font-bold ${s.color}`} style={{ fontFamily: 'Space Grotesk' }}>
                        {tripsLoading ? '...' : s.value}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">{s.label}</div>
                    </div>
                  ))}
                </div>
                <div className="rounded-xl border border-slate-100 overflow-hidden">
                  <div className="bg-slate-50 px-4 py-3 border-b border-slate-100">
                    <h3 className="text-sm font-semibold text-slate-700">Avslutade resor — Intjäning</h3>
                  </div>
                  {tripsLoading ? (
                    <div className="p-4 space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
                  ) : completed.length === 0 ? (
                    <div className="py-8 text-center text-sm text-slate-400">Inga avslutade resor ännu</div>
                  ) : (
                    <div className="divide-y divide-slate-50">
                      {completed.map(t => (
                        <div key={t.id} className="px-4 py-3 flex justify-between items-center hover:bg-slate-50 transition text-sm">
                          <div>
                            <div className="text-xs font-medium text-slate-700 truncate max-w-[220px]">
                              {t.origin_address ?? '—'} → {t.dest_address ?? '—'}
                            </div>
                            <div className="text-xs text-slate-400 mt-0.5">
                              {t.ended_at ? new Date(t.ended_at).toLocaleString('sv-SE') : new Date(t.created_at).toLocaleDateString('sv-SE')}
                            </div>
                          </div>
                          <span className="font-bold text-emerald-600 shrink-0 ml-4">
                            +{t.fare?.toFixed(0) ?? '0'} kr
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Transaction ledger — live sync with mobile Ekonomi */}
                <div className="rounded-xl border border-slate-100 overflow-hidden">
                  <div className="bg-slate-50 px-4 py-3 border-b border-slate-100">
                    <h3 className="text-sm font-semibold text-slate-700">Transaktioner (live)</h3>
                  </div>
                  {txLoading ? (
                    <div className="p-4 space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
                  ) : transactions.length === 0 ? (
                    <div className="py-8 text-center text-sm text-slate-400">Inga transaktioner ännu</div>
                  ) : (
                    <div className="divide-y divide-slate-50 max-h-80 overflow-y-auto">
                      {transactions.slice(0, 30).map(tx => (
                        <div key={tx.id} className="px-4 py-3 flex justify-between items-center text-sm hover:bg-slate-50">
                          <div>
                            <div className="text-xs font-medium text-slate-700">{tx.note ?? tx.category}</div>
                            <div className="text-xs text-slate-400 mt-0.5">
                              {new Date(tx.occurred_at).toLocaleString('sv-SE')}
                              {tx.trip_id && <span className="ml-2 text-emerald-600">· Verifierad</span>}
                            </div>
                          </div>
                          <span className={`font-bold shrink-0 ml-4 ${tx.type === 'income' ? 'text-emerald-600' : 'text-red-500'}`}>
                            {tx.type === 'income' ? '+' : '-'}{tx.amount.toFixed(0)} kr
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Vilotidsrapport ── */}
            {activeTab === 'vilotid' && (
              <div className="space-y-5">
                {/* 4 KPI cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="p-4 rounded-xl border border-slate-100 bg-white">
                    <div className="text-xs text-slate-500 mb-1 flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-electric-400" />Körtid idag
                    </div>
                    <div className="text-xl font-bold text-slate-800">{formatDuration(todayMinutes)}</div>
                    <div className="text-xs text-slate-400 mt-0.5">Max 13h/dag</div>
                  </div>
                  <div className="p-4 rounded-xl border border-slate-100 bg-white">
                    <div className="text-xs text-slate-500 mb-1 flex items-center gap-1.5">
                      <Car className="w-3.5 h-3.5 text-blue-400" />Körpass idag
                    </div>
                    <div className="text-xl font-bold text-slate-800">{todaySessions.length}</div>
                    <div className="text-xs text-slate-400 mt-0.5">aktiva pass</div>
                  </div>
                  <div className="p-4 rounded-xl border border-slate-100 bg-white">
                    <div className="text-xs text-slate-500 mb-1 flex items-center gap-1.5">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />Vilotid idag
                    </div>
                    <div className="text-xl font-bold text-slate-800">{formatDuration(todayBreakMin)}</div>
                    <div className="text-xs text-slate-400 mt-0.5">30 min krävs efter 6h</div>
                  </div>
                  <div className="p-4 rounded-xl border border-slate-100 bg-white flex flex-col justify-between">
                    <div className="text-xs text-slate-500 mb-2 flex items-center gap-1.5">
                      <ShieldCheck className="w-3.5 h-3.5 text-slate-400" />Regelefterlevnad
                    </div>
                    {sessionsLoading
                      ? <Skeleton className="h-7 w-28" />
                      : <CompliancePill status={complianceStatus} />
                    }
                  </div>
                </div>

                {/* Today's sessions */}
                <div className="rounded-xl border border-slate-100 overflow-hidden">
                  <div className="bg-slate-50 px-4 py-3 border-b border-slate-100">
                    <h3 className="font-semibold text-slate-700 text-sm">Körpass idag</h3>
                  </div>
                  {sessionsLoading ? (
                    <div className="p-4 space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
                  ) : todaySessions.length === 0 ? (
                    <div className="py-8 text-center text-sm text-slate-400">Inga körpass registrerade idag</div>
                  ) : (
                    <div className="divide-y divide-slate-50">
                      {todaySessions.map(session => {
                        const open = expandedSession === session.id;
                        const breaks = session.breaks ?? [];
                        const sessionBreakMin = breaks.reduce((s, b) => s + breakDurationMin(b), 0);
                        const sessionStatus: ComplianceStatus = (() => {
                          const maxBreak = breaks.reduce((m, b) => Math.max(m, breakDurationMin(b)), 0);
                          if (session.driving_minutes > 780) return 'violation';
                          if (session.driving_minutes > 540 && maxBreak < 45) return 'warning';
                          if (session.driving_minutes > 360 && maxBreak < 30) return 'warning';
                          return 'ok';
                        })();
                        const cfg = COMPLIANCE_CONFIG[sessionStatus];
                        return (
                          <div key={session.id}>
                            <button
                              onClick={() => setExpandedSession(open ? null : session.id)}
                              className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition text-sm text-left"
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-2 h-2 rounded-full ${session.status === 'active' ? 'bg-emerald-400 animate-pulse' : 'bg-slate-300'}`} />
                                <div>
                                  <div className="font-medium text-slate-700">
                                    {new Date(session.started_at).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                                    {session.ended_at && ` – ${new Date(session.ended_at).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}`}
                                    {session.status === 'active' && <span className="ml-2 text-xs text-emerald-500 font-medium">Pågår</span>}
                                  </div>
                                  <div className="text-xs text-slate-400">
                                    Körtid: {formatDuration(session.driving_minutes)} · Vilotid: {formatDuration(sessionBreakMin)} · {breaks.length} rast{breaks.length !== 1 ? 'er' : ''}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className={`inline-flex items-center gap-1 text-xs font-medium rounded-md px-2 py-0.5 border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
                                  <cfg.Icon className="w-3 h-3" />{cfg.label}
                                </span>
                                {open
                                  ? <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                                  : <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                }
                              </div>
                            </button>
                            {open && breaks.length > 0 && (
                              <div className="px-6 pb-3 space-y-1.5">
                                {breaks.map(b => (
                                  <div key={b.id} className="flex items-center justify-between text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2">
                                    <span className={`font-medium ${b.type === 'lunch' ? 'text-emerald-600' : 'text-blue-500'}`}>
                                      {b.type === 'lunch' ? 'Lunch' : 'Paus'}
                                    </span>
                                    <span>
                                      {new Date(b.started_at).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                                      {b.ended_at && ` – ${new Date(b.ended_at).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}`}
                                    </span>
                                    <span className="font-medium text-slate-600">{formatDuration(breakDurationMin(b))}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {open && breaks.length === 0 && (
                              <div className="px-6 pb-3 text-xs text-slate-400">Inga raster registrerade för detta pass</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <p className="text-xs text-slate-400 text-center">
                  Regelefterlevnad enligt Vägarbetstidslagen (2005:395) · Max 13h/dag, 30 min vilotid efter 6h, 45 min efter 9h
                </p>
              </div>
            )}

            {/* ── DG-Plånbok ── */}
            {activeTab === 'planbok' && (
              <div className="space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-700">Förarens Digitala Plånbok</h3>
                    <p className="text-xs text-slate-400 mt-0.5">Legitimationer och dokument kopplade till ditt förarkonto</p>
                  </div>
                  <span className="text-xs text-slate-400 bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-lg">
                    {walletItems.length} dokument
                  </span>
                </div>

                {walletLoading ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 w-full" />)}
                  </div>
                ) : walletItems.length === 0 ? (
                  <div className="py-12 text-center space-y-2">
                    <CreditCard className="w-10 h-10 text-slate-200 mx-auto" />
                    <p className="text-sm text-slate-500 font-medium">Inga dokument registrerade ännu</p>
                    <p className="text-xs text-slate-400">Lägg till via mobilappen</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {walletItems.map(item => {
                      const cfg = WALLET_KIND_CONFIG[item.kind] ?? {
                        label: item.kind, color: 'text-slate-600', bg: 'bg-slate-50', Icon: CreditCard,
                      };
                      const maskedNumber = item.number
                        ? item.number.length > 4 ? `•••• ${item.number.slice(-4)}` : item.number
                        : null;
                      return (
                        <div key={item.id}
                          className="p-5 rounded-xl border border-slate-100 bg-white hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-200 flex items-start gap-4">
                          <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${cfg.bg}`}>
                            <cfg.Icon className={`w-5 h-5 ${cfg.color}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{cfg.label}</span>
                              <ExpiryBadge expiresAt={item.expires_at} />
                            </div>
                            <div className="font-semibold text-slate-800 text-sm truncate">{item.title}</div>
                            {maskedNumber && (
                              <div className="text-xs text-slate-400 mt-0.5 font-mono">{maskedNumber}</div>
                            )}
                            {item.expires_at && (
                              <div className="text-xs text-slate-400 mt-1">
                                Giltig t.o.m. {new Date(item.expires_at).toLocaleDateString('sv-SE')}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Verification summary */}
                {walletItems.length > 0 && (() => {
                  const now = Date.now();
                  const expired = walletItems.filter(w => w.expires_at && new Date(w.expires_at).getTime() < now);
                  const expiringSoon = walletItems.filter(w => {
                    if (!w.expires_at) return false;
                    const d = (new Date(w.expires_at).getTime() - now) / 86_400_000;
                    return d >= 0 && d <= 30;
                  });
                  if (expired.length === 0 && expiringSoon.length === 0) return null;
                  return (
                    <div className="p-4 rounded-xl border border-amber-100 bg-amber-50 space-y-1">
                      <div className="flex items-center gap-2 text-amber-700 font-semibold text-sm">
                        <AlertTriangle className="w-4 h-4" />Uppmärksamhet krävs
                      </div>
                      {expired.length > 0 && (
                        <p className="text-xs text-amber-600">{expired.length} dokument har utgått och måste förnyas.</p>
                      )}
                      {expiringSoon.length > 0 && (
                        <p className="text-xs text-amber-600">{expiringSoon.length} dokument löper ut inom 30 dagar.</p>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DriverPortal;
